import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError, fieldErrorProps } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import type { FieldErrors } from '@/lib/validationErrors';
import {
  PROGRAMMING_CONTENT_SCORING_TYPE_OPTIONS,
  PROGRAMMING_SCORING_TYPE_OPTIONS,
  type ScoringDirection,
  getProgrammingScoringDirection,
  getProgrammingScoringLabel,
} from '@ergshoot/shared';
import { Dumbbell, Loader2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

export interface WorkoutFormData {
  name: string;
  description: string;
  scheduled_date: string;
  scoring_type: string;
  scoring_direction: string;
  category: string;
  movements: string;
  equipment: string;
  track_id: string;
  /** Whether an owner's workout is routed to gym programming or kept personal. */
  publish_to_gym?: boolean;
  /** FEAT-APR04-004: share to gym community feed (members) */
  shared_to_gym?: boolean;
  /** FEAT-APR04-004: share to global community feed (members/individuals) */
  shared_to_global?: boolean;
  /** Keep this workout in the creator's personal My Workouts favourites. */
  is_favorite?: boolean;
  /** Mark a personal workout as a repeatable benchmark. */
  /** @deprecated Use is_favorite; the database column remains benchmark_flag. */
  benchmark_flag?: boolean;
}

interface TrackOption {
  id: string;
  name: string;
}

export type WorkoutFormVariant = 'daily' | 'standard' | 'content';

interface WorkoutFormProps {
  /** Field configuration for the calling programming surface. */
  variant?: WorkoutFormVariant;
  /** Title shown in the form header */
  title?: string;
  /** Subtitle shown in the form header */
  subtitle?: string;
  /** Available tracks for the track selector. If empty, track selector is hidden. */
  tracks?: TrackOption[];
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Called when the form is submitted */
  onSubmit: (data: WorkoutFormData) => void;
  /** Called when the form is cancelled */
  onCancel: () => void;
  fieldErrors?: FieldErrors;
  /** Initial values for editing an existing workout */
  initialValues?: Partial<WorkoutFormData>;
  /** Caller-specific default when no stored direction is supplied. */
  defaultScoringDirection?: ScoringDirection;
  /** Submit button label */
  submitLabel?: string;
  /**
   * FEAT-APR04-004 — which visibility controls to render. Different roles
   * see different controls:
   *   'owner'      → explicit gym programming / personal workout destination
   *   'member'     → "Share to [Gym] community" + "Share to global community"
   *   'individual' → "Share to global community"
   *   'personal'   → personal My Workouts + optional global Community publishing
   *   'none' (default) → no controls (used by HQ Admin where visibility is implicit)
   */
  visibilityMode?: 'none' | 'owner' | 'member' | 'individual' | 'personal';
  /** Show the personal Favourite control. */
  showFavorite?: boolean;
  /** @deprecated Use showFavorite. */
  showBenchmark?: boolean;
  /** Gym name to use in "Share to [Gym] community" labels */
  gymName?: string;
  /** Native minimum for scheduled date, used by new HQ daily workouts. */
  minimumScheduledDate?: string;
  /** Optional caller-specific heading icon. */
  headerIcon?: ReactNode;
  /** Optional DOM ids for callers with established validation and E2E selectors. */
  fieldIds?: Partial<
    Record<
      | 'name'
      | 'destination'
      | 'scheduledDate'
      | 'track'
      | 'scoringType'
      | 'scoringDirection'
      | 'category'
      | 'description'
      | 'movements'
      | 'equipment',
      string
    >
  >;
}

const SCORING_DIRECTIONS = [
  { value: 'ascending', label: 'Lower is Better' },
  { value: 'descending', label: 'Higher is Better' },
];

const WOD_STYLES = [
  { value: '', label: 'Select style...' },
  { value: 'single_erg', label: 'Single Erg' },
  { value: 'dual_erg', label: 'Dual Erg' },
  { value: 'medley', label: 'Medley (three ergs)' },
  { value: 'hybrid', label: 'Hybrid (functional & shooting)' },
  { value: 'team_relay', label: 'Team Relay' },
  { value: 'endurance', label: 'Endurance' },
  { value: 'standard', label: 'Standard' },
];

const STANDARD_STYLES = [
  { value: '', label: 'Select style...' },
  { value: 'short_sprint', label: 'Short Sprint' },
  { value: 'long_sprint', label: 'Long Sprint' },
  { value: 'middle_distance', label: 'Middle Distance' },
  { value: 'enduro_sprint', label: 'Enduro Sprint' },
  { value: 'enduro_long', label: 'Enduro Long' },
  { value: 'enduro_medley', label: 'Enduro Medley' },
];

const VARIANT_CONFIG = {
  daily: {
    showDate: true,
    showCategory: true,
    categoryLabel: 'WOD Style',
    categoryOptions: WOD_STYLES,
    descriptionRequired: true,
    titleLabel: 'Title',
    titlePlaceholder: 'e.g., 5 x 500m Row on 3min rest between efforts',
    movementsLabel: 'Workout (one per line)',
    movementsPlaceholder: '5 Rounds:\n500m Row\n21 Thrusters (95/65 lb)\n21 Pull-ups',
    movementsMinHeight: 'min-h-[120px]',
    equipmentPlaceholder: 'Barbell, Pull-up Bar, Rower',
    descriptionPlaceholder: 'Short description of the workout, rules, and time caps...',
    descriptionMinHeight: 'min-h-[120px]',
    descriptionLast: false,
    pairedScoringLayout: false,
    scoringTypeOptions: PROGRAMMING_SCORING_TYPE_OPTIONS,
    autoDirection: false,
    defaultDirection: 'ascending',
    closeButtonSize: 'icon',
    secondaryActionFirst: false,
  },
  standard: {
    showDate: false,
    showCategory: true,
    categoryLabel: 'Standard Style',
    categoryOptions: STANDARD_STYLES,
    descriptionRequired: false,
    titleLabel: 'Title',
    titlePlaceholder: 'e.g., 5 x 500m Row on 3min rest between efforts',
    movementsLabel: 'Workout (one per line)',
    movementsPlaceholder: '500m Row\n5 Target Shots\n20 Burpees',
    movementsMinHeight: 'min-h-[100px]',
    equipmentPlaceholder: 'Rower, ErgShoot Target, Pull-up Bar',
    descriptionPlaceholder: 'Full workout description, rules, and time caps...',
    descriptionMinHeight: 'min-h-[100px]',
    descriptionLast: true,
    pairedScoringLayout: true,
    scoringTypeOptions: PROGRAMMING_SCORING_TYPE_OPTIONS,
    autoDirection: true,
    defaultDirection: 'ascending',
    closeButtonSize: 'sm',
    secondaryActionFirst: true,
  },
  content: {
    showDate: false,
    showCategory: false,
    categoryLabel: '',
    categoryOptions: [],
    descriptionRequired: false,
    titleLabel: 'Name',
    titlePlaceholder: 'e.g., Fran, Grace, 2K Row',
    movementsLabel: 'Movements (one per line)',
    movementsPlaceholder: '21 Thrusters (95/65)\n21 Pull-ups\n15 Thrusters\n15 Pull-ups',
    movementsMinHeight: 'min-h-[100px]',
    equipmentPlaceholder: 'Barbell, Pull-up Bar, Rower',
    descriptionPlaceholder: 'Full workout description and rules...',
    descriptionMinHeight: 'min-h-[80px]',
    descriptionLast: true,
    pairedScoringLayout: true,
    scoringTypeOptions: PROGRAMMING_CONTENT_SCORING_TYPE_OPTIONS,
    autoDirection: false,
    defaultDirection: 'ascending',
    closeButtonSize: 'sm',
    secondaryActionFirst: true,
  },
} as const;

export interface NormalizedWorkoutFormData {
  name: string;
  description: string;
  scheduled_date: string;
  scoring_type: string;
  scoring_direction: string;
  category?: string;
  movements: string[];
  equipment: string[];
  track_id?: string;
  publish_to_gym?: boolean;
  shared_to_gym?: boolean;
  shared_to_global?: boolean;
  is_favorite?: boolean;
  benchmark_flag?: boolean;
}

export function normalizeWorkoutFormData(data: WorkoutFormData): NormalizedWorkoutFormData {
  const normalized: NormalizedWorkoutFormData = {
    name: data.name.trim(),
    description: data.description.trim(),
    scheduled_date: data.scheduled_date,
    scoring_type: data.scoring_type,
    scoring_direction: data.scoring_direction,
    category: data.category || undefined,
    movements: data.movements
      .split('\n')
      .map((movement) => movement.trim())
      .filter(Boolean),
    equipment: data.equipment
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    track_id: data.track_id || undefined,
  };

  if (data.publish_to_gym !== undefined) normalized.publish_to_gym = data.publish_to_gym;
  if (data.shared_to_gym !== undefined) normalized.shared_to_gym = data.shared_to_gym;
  if (data.shared_to_global !== undefined) normalized.shared_to_global = data.shared_to_global;
  if (data.is_favorite !== undefined) normalized.is_favorite = data.is_favorite;
  if (data.benchmark_flag !== undefined) normalized.benchmark_flag = data.benchmark_flag;

  return normalized;
}

/**
 * Shared workout creation/editing form used across all portals.
 * Provides the full field set matching the HQ admin form.
 */
export function WorkoutForm({
  variant = 'daily',
  title = 'Create Workout',
  subtitle = 'Create a new workout',
  tracks = [],
  isSubmitting = false,
  onSubmit,
  onCancel,
  fieldErrors,
  initialValues,
  defaultScoringDirection,
  submitLabel = 'Create Workout',
  visibilityMode = 'none',
  showFavorite = false,
  showBenchmark = false,
  gymName,
  minimumScheduledDate,
  headerIcon,
  fieldIds,
}: WorkoutFormProps) {
  const config = VARIANT_CONFIG[variant];
  const ids = {
    name: fieldIds?.name ?? 'wf-name',
    destination: fieldIds?.destination ?? 'wf-destination',
    scheduledDate: fieldIds?.scheduledDate ?? 'wf-date',
    track: fieldIds?.track ?? 'wf-track',
    scoringType: fieldIds?.scoringType ?? 'wf-scoring-type',
    scoringDirection: fieldIds?.scoringDirection ?? 'wf-scoring-direction',
    category: fieldIds?.category ?? 'wf-category',
    description: fieldIds?.description ?? 'wf-description',
    movements: fieldIds?.movements ?? 'wf-movements',
    equipment: fieldIds?.equipment ?? 'wf-equipment',
  };
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [scheduledDate, setScheduledDate] = useState(initialValues?.scheduled_date || '');
  const [scoringType, setScoringType] = useState(initialValues?.scoring_type || 'time');
  const [scoringDirection, setScoringDirection] = useState(
    initialValues?.scoring_direction || defaultScoringDirection || config.defaultDirection
  );
  const [category, setCategory] = useState(initialValues?.category || '');
  const [movements, setMovements] = useState(initialValues?.movements || '');
  const [equipment, setEquipment] = useState(initialValues?.equipment || '');
  const [trackId, setTrackId] = useState(initialValues?.track_id || '');
  // FEAT-APR04-004 visibility controls. Owners select a destination; the API
  // continues to receive the established publish_to_gym boolean contract.
  const [publishToGym, setPublishToGym] = useState<boolean>(initialValues?.publish_to_gym ?? true);
  const [sharedToGym, setSharedToGym] = useState<boolean>(initialValues?.shared_to_gym ?? false);
  const [sharedToGlobal, setSharedToGlobal] = useState<boolean>(
    initialValues?.shared_to_global ?? false
  );
  const [benchmarkFlag, setBenchmarkFlag] = useState<boolean>(
    initialValues?.is_favorite ?? initialValues?.benchmark_flag ?? false
  );
  const showFavoriteControl = showFavorite || showBenchmark;

  const trackOptions = [
    { value: '', label: 'Select a track...' },
    ...tracks.map((t) => ({ value: t.id, label: t.name })),
  ];
  const scoringTypeOptions = config.scoringTypeOptions.some(
    (option) => option.value === scoringType
  )
    ? config.scoringTypeOptions
    : [
        ...config.scoringTypeOptions,
        { value: scoringType, label: getProgrammingScoringLabel(scoringType) },
      ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      description,
      scheduled_date: scheduledDate,
      scoring_type: scoringType,
      scoring_direction: scoringDirection,
      category,
      movements,
      equipment,
      track_id: trackId,
      publish_to_gym:
        visibilityMode === 'owner'
          ? publishToGym
          : visibilityMode === 'personal'
            ? false
            : undefined,
      shared_to_gym: visibilityMode === 'member' ? sharedToGym : undefined,
      shared_to_global: visibilityMode !== 'none' ? sharedToGlobal : undefined,
      is_favorite: showFavoriteControl && visibilityMode !== 'none' ? benchmarkFlag : undefined,
      benchmark_flag: showBenchmark ? benchmarkFlag : undefined,
    });
  };

  const trackField =
    tracks.length > 0 ? (
      <div className="space-y-2">
        <Label htmlFor={ids.track}>Track</Label>
        <Select
          id={ids.track}
          options={trackOptions}
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
        />
      </div>
    ) : null;

  const scoringTypeField = (
    <div className="space-y-2">
      <Label htmlFor={ids.scoringType}>Scoring Type</Label>
      <Select
        id={ids.scoringType}
        options={[...scoringTypeOptions]}
        value={scoringType}
        onChange={(e) => {
          setScoringType(e.target.value);
          if (config.autoDirection) {
            setScoringDirection(getProgrammingScoringDirection(e.target.value));
          }
        }}
      />
    </div>
  );

  const scoringDirectionField = (
    <div className="space-y-2">
      <Label htmlFor={ids.scoringDirection}>Scoring Direction</Label>
      <Select
        id={ids.scoringDirection}
        options={SCORING_DIRECTIONS}
        value={scoringDirection}
        onChange={(e) => setScoringDirection(e.target.value)}
      />
    </div>
  );

  const categoryField = config.showCategory ? (
    <div className="space-y-2">
      <Label htmlFor={ids.category}>{config.categoryLabel}</Label>
      <Select
        id={ids.category}
        options={[...config.categoryOptions]}
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      />
    </div>
  ) : null;

  const descriptionField = (
    <div className="space-y-2">
      <Label htmlFor={ids.description}>Description{config.descriptionRequired ? ' *' : ''}</Label>
      <textarea
        id={ids.description}
        className={`flex ${config.descriptionMinHeight} w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
        placeholder={config.descriptionPlaceholder}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        required={config.descriptionRequired}
        {...fieldErrorProps(fieldErrors, 'description')}
      />
      <FieldError errors={fieldErrors} field="description" />
    </div>
  );

  const movementsField = (
    <div className="space-y-2">
      <Label htmlFor={ids.movements}>{config.movementsLabel}</Label>
      <textarea
        id={ids.movements}
        className={`flex ${config.movementsMinHeight} w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
        placeholder={config.movementsPlaceholder}
        value={movements}
        onChange={(e) => setMovements(e.target.value)}
      />
    </div>
  );

  const equipmentField = (
    <div className="space-y-2">
      <Label htmlFor={ids.equipment}>Equipment (comma separated)</Label>
      <Input
        id={ids.equipment}
        placeholder={config.equipmentPlaceholder}
        value={equipment}
        onChange={(e) => setEquipment(e.target.value)}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {headerIcon ?? <Dumbbell className="h-5 w-5 text-primary" />}
              {title}
            </CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </div>
          <Button
            variant="ghost"
            size={config.closeButtonSize}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={ids.name}>{config.titleLabel} *</Label>
            <Input
              id={ids.name}
              placeholder={config.titlePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              {...fieldErrorProps(fieldErrors, 'name')}
            />
            <FieldError errors={fieldErrors} field="name" />
          </div>

          {config.showDate && (
            <div className="space-y-2">
              <Label htmlFor={ids.scheduledDate}>Scheduled Date *</Label>
              <Input
                id={ids.scheduledDate}
                type="date"
                value={scheduledDate}
                onInput={(e) => setScheduledDate(e.currentTarget.value)}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={minimumScheduledDate}
                required
                {...fieldErrorProps(fieldErrors, 'scheduled_date')}
              />
              <FieldError errors={fieldErrors} field="scheduled_date" />
            </div>
          )}

          {config.pairedScoringLayout ? (
            <>
              {trackField}
              <div className="grid gap-4 sm:grid-cols-2">
                {scoringTypeField}
                {scoringDirectionField}
              </div>
              {categoryField}
            </>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {trackField}
                {scoringTypeField}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {scoringDirectionField}
                {categoryField}
              </div>
            </>
          )}

          {config.descriptionLast ? (
            <>
              {movementsField}
              {equipmentField}
              {descriptionField}
            </>
          ) : (
            <>
              {descriptionField}
              {movementsField}
              {equipmentField}
            </>
          )}

          {/* FEAT-APR04-004 visibility controls */}
          {visibilityMode !== 'none' && (
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
              {visibilityMode === 'owner' && (
                <>
                  <Label htmlFor={ids.destination} className="text-sm font-semibold">
                    Destination
                  </Label>
                  <Select
                    id={ids.destination}
                    options={[
                      { value: 'gym', label: 'Gym Programming (Daily WODs)' },
                      { value: 'personal', label: 'Personal Workout (My Workouts)' },
                    ]}
                    value={publishToGym ? 'gym' : 'personal'}
                    onChange={(event) => {
                      const nextPublishToGym = event.target.value === 'gym';
                      setPublishToGym(nextPublishToGym);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    {publishToGym
                      ? 'Publishes to your gym schedule for all members.'
                      : 'Saves this workout to My Workouts. You can optionally add it to Community Workouts.'}
                  </p>
                  <label className="flex cursor-pointer items-center gap-2 pt-1">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sharedToGlobal}
                      onChange={(event) => setSharedToGlobal(event.target.checked)}
                    />
                    <span className="text-sm">Add to Community Workouts</span>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Off by default. Turn this on when the workout may be viewed outside your gym.
                  </p>
                </>
              )}
              {visibilityMode === 'personal' && (
                <>
                  <Label className="text-sm font-semibold">Destination</Label>
                  <p className="text-xs text-muted-foreground">
                    Saved to My Workouts. You can optionally add it to Community Workouts.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sharedToGlobal}
                      onChange={(e) => setSharedToGlobal(e.target.checked)}
                    />
                    <span className="text-sm">Add to Community Workouts</span>
                  </label>
                </>
              )}
              {visibilityMode === 'member' && (
                <>
                  <Label className="text-sm font-semibold">Visibility</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sharedToGym}
                      onChange={(e) => setSharedToGym(e.target.checked)}
                    />
                    <span className="text-sm">
                      Share to {gymName ? `${gymName} ` : 'my gym '}community feed
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sharedToGlobal}
                      onChange={(e) => setSharedToGlobal(e.target.checked)}
                    />
                    <span className="text-sm">Add to Community Workouts</span>
                  </label>
                </>
              )}
              {visibilityMode === 'individual' && (
                <>
                  <Label className="text-sm font-semibold">Visibility</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sharedToGlobal}
                      onChange={(e) => setSharedToGlobal(e.target.checked)}
                    />
                    <span className="text-sm">Add to Community Workouts</span>
                  </label>
                </>
              )}
              {visibilityMode !== 'owner' && (
                <p className="text-xs text-muted-foreground">
                  Workouts are private by default. Tick a box above to share.
                </p>
              )}
            </div>
          )}

          {showFavoriteControl && visibilityMode !== 'none' && (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  aria-label="Favourite"
                  className="mt-0.5 h-4 w-4"
                  checked={benchmarkFlag}
                  onChange={(event) => setBenchmarkFlag(event.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium">Favourite</span>
                  <span className="block text-xs text-muted-foreground">
                    Keep this workout in your personal Favourites.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            {config.secondaryActionFirst && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" className="flex-1" disabled={isSubmitting || !name.trim()}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitLabel}
            </Button>
            {!config.secondaryActionFirst && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
